/**
 * F-073 — Client-side score signer (HMAC-SHA256 via Web Crypto).
 *
 * The signature alone doesn't make scores tamper-proof — see security
 * notes in the F-073 plan. It's one of four layers; combined with the
 * server-issued single-use nonce, sanity bounds, and rate limit, raising
 * any score by ≈ 1 reliably without our cooperation is impractical.
 *
 * Message format (must match the Edge Function exactly):
 *   `${user_id}|${score}|${merges}|${duration_ms}|${peak_tier}|${nonce}`
 *
 * peak_tier (F-086) is empty string when omitted by older clients.
 */

export interface SignPayload {
  userId: string;
  score: number;
  merges: number;
  durationMs: number;
  /** F-086 — best tier reached (0..8). Omit for backward compat. */
  peakTier?: number;
  nonce: string;
}

export function buildSignatureMessage(p: SignPayload): string {
  const peak = p.peakTier === undefined ? "" : String(p.peakTier);
  return `${p.userId}|${p.score}|${p.merges}|${p.durationMs}|${peak}|${p.nonce}`;
}

export async function signScore(
  secret: string,
  payload: SignPayload,
): Promise<string> {
  if (!secret) throw new Error("score_signer: missing secret");
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign(
    "HMAC",
    key,
    enc.encode(buildSignatureMessage(payload)),
  );
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
